import { Button, ButtonProps, Flex, useBoolean } from '@chakra-ui/react';
import { ReactComponent as OpenFolderIcon } from 'assets/icons/folderOpen.svg';
import { IMAGE_FOLDER_MODAL } from 'constants/mixpanel';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { useUser } from 'modules/User';
import { ReactElement, memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { UploadImagesData } from '../UploadMenu.helper';
import { FolderProps } from './Folders.helper';
import FoldersList from './FoldersList';
import { ManageFolderModal } from './ManageFolderModal';

export const ManageFoldersButton = (props: Omit<ButtonProps, 'ref'>): ReactElement => {
  const { t } = useTranslation('editor_widget_menu', { useSuspense: false });
  return (
    <Button
      leftIcon={<OpenFolderIcon />}
      size='sm'
      variant='outline'
      {...props}
      data-testid='manage-folders-btn'
      borderRadius='var(--vg-radii-base)'
    >
      {t('sideMenu.imagesMenu.folders.manageFolders')}
    </Button>
  );
};

interface ManageFolderMenuProps {
  usersFolders: FolderProps[];
  uploadImages: UploadImagesData;
  selectedFolder: string | number;
  handleChangeFolder: (selectedFolder: string | number) => void;
  onClickUpgradeModal: () => void;
}

/**
 * Main component to handle uploads
 */
export const ManageFolders = memo(
  ({
    usersFolders,
    uploadImages,
    handleChangeFolder,
    selectedFolder,
    onClickUpgradeModal,
  }: ManageFolderMenuProps): ReactElement => {
    const [modal, toggleModal] = useBoolean(false);
    const [createOnOpen, toggleCreate] = useBoolean(false);
    const { isBusinessUser, isEnterpriseUser } = useUser();

    /**
     * This action open the modal and automatically creates a folder
     */
    const handleOpenToCreate = useCallback(() => {
      toggleModal.on();
      toggleCreate.on();

      Mixpanel.track(IMAGE_FOLDER_MODAL.eventName);
    }, [toggleCreate, toggleModal]);

    const allowFolders = isBusinessUser || isEnterpriseUser;

    /**
     * handler to open the uploads modal
     */
    const handleOpenModal = useCallback(() => {
      if (!allowFolders) {
        onClickUpgradeModal();
        return;
      }
      toggleCreate.off();
      toggleModal.on();
      Mixpanel.track(IMAGE_FOLDER_MODAL.eventName);
    }, [toggleCreate, toggleModal, allowFolders, onClickUpgradeModal]);

    return (
      <Flex gap='11px' alignItems='center' mx='4' mt='-4px' data-testid='image-manage-folders-container'>
        <FoldersList
          usersFolders={usersFolders}
          value={selectedFolder}
          allowCreate={allowFolders}
          onChange={handleChangeFolder}
          onClickUpgradeModal={onClickUpgradeModal}
          onClickNewFolder={handleOpenToCreate}
        />

        <ManageFoldersButton onClick={handleOpenModal} />
        <ManageFolderModal
          uploadImages={uploadImages}
          usersFolders={usersFolders}
          isOpen={modal}
          onClose={toggleModal.off}
          createOnOpen={createOnOpen}
        />
      </Flex>
    );
  },
);
ManageFolders.displayName = 'ManageFolders';
